import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface LoginProps {
  userType: 'player' | 'admin';
}

const Login: React.FC<LoginProps> = ({ userType }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await login(email, password, userType);

      // Redirect based on user type
      if (userType === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/player/dashboard');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const isAdmin = userType === 'admin';
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');

  // "No email" support-ticket flow (players only)
  const [showNoEmail, setShowNoEmail] = useState(false);
  const [noEmailName, setNoEmailName] = useState('');
  const [noEmailId, setNoEmailId] = useState('');
  const [noEmailNewEmail, setNoEmailNewEmail] = useState('');
  const [noEmailLoading, setNoEmailLoading] = useState(false);
  const [noEmailError, setNoEmailError] = useState('');
  const [noEmailSuccess, setNoEmailSuccess] = useState('');

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError('');
    setForgotSuccess('');
    try {
      const res = await fetch('/api/auth/request-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail, type: userType }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('auth.forgotPasswordFailed'));
      setForgotSuccess(t('auth.resetLinkSent'));
    } catch (err: any) {
      setForgotError(err.message || t('auth.forgotPasswordFailed'));
    } finally {
      setForgotLoading(false);
    }
  };

  const handleNoEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNoEmailLoading(true);
    setNoEmailError('');
    setNoEmailSuccess('');
    try {
      const res = await fetch('/api/auth/request-email-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // User types Bowler ID (player_id + 100) — convert back to player_id for the backend
        body: JSON.stringify({ playerId: String(Number(noEmailId) - 100), name: noEmailName, newEmail: noEmailNewEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to submit request.');
      setNoEmailSuccess(data.message);
      setNoEmailName(''); setNoEmailId(''); setNoEmailNewEmail('');
    } catch (err: any) {
      setNoEmailError(err.message || 'Failed to submit request.');
    } finally {
      setNoEmailLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
       

        {/* Login Form */}
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20">
         {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img 
            src="/BowlerProLogo1.png" 
            alt="BowlerPro Logo" 
            className="mx-auto max-h-40 w-auto"
          />
          </Link>
          <h2 className={`text-2xl font-bold ${isAdmin ? 'text-purple-400' : 'text-blue-400'}`}>
            {isAdmin ? t('auth.adminLogin') : t('auth.playerLogin')}
          </h2>
        </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email / ID Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                {isAdmin ? t('auth.emailAddress') : 'Email or Player ID'}
              </label>
              <input
                id="email"
                type={isAdmin ? 'email' : 'text'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={isAdmin ? t('auth.enterEmail') : 'Email address or Player ID'}
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                {t('auth.password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('auth.enterPassword')}
              />
              <div className="text-right mt-2">
                <button
                  type="button"
                  className="text-blue-400 hover:text-blue-300 text-sm underline"
                  onClick={() => setShowForgotModal(true)}
                >
                  {t('auth.forgotPassword')}
                </button>
              </div>
            </div>


            {/* Error Message */}
            {error && (
              <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition duration-200 ${isAdmin
                ? 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500'
                : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isLoading ? t('auth.signingIn') : t('auth.signIn')}
            </button>
          </form>

          {/* Links */}
          <div className="mt-6 text-center space-y-3">
            <Link
              to={`/signup/${userType}`}
              className={`block ${isAdmin ? 'text-purple-400 hover:text-purple-300' : 'text-blue-400 hover:text-blue-300'} text-sm transition duration-200`}
            >
              {t('auth.noAccount')}
            </Link>

            <Link
              to="/"
              className="block text-gray-400 hover:text-white text-sm transition duration-200"
            >
              ← {t('common.backToHome')}
            </Link>

            {!isAdmin && (
              <Link
                to="/login/admin"
                className="block text-gray-400 hover:text-white text-sm transition duration-200"
              >
                {t('auth.areYouAdmin')}
              </Link>
            )}

            {isAdmin && (
              <Link
                to="/login/player"
                className="block text-gray-400 hover:text-white text-sm transition duration-200"
              >
                {t('auth.areYouPlayer')}
              </Link>
            )}
          </div>
        </div>
        {/* Forgot Password Modal */}
        {showForgotModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="bg-gray-900 rounded-xl shadow-lg p-8 max-w-sm w-full border border-white/20 relative">
              <button
                className="absolute top-3 right-3 text-gray-400 hover:text-white"
                onClick={() => {
                  setShowForgotModal(false);
                  setForgotEmail('');
                  setForgotError('');
                  setForgotSuccess('');
                }}
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h3 className="text-xl font-bold text-white mb-4 text-center">{t('auth.forgotPasswordTitle')}</h3>
              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <div>
                  <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-300 mb-2">
                    {t('auth.emailAddress')}
                  </label>
                  <input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('auth.enterEmail')}
                  />
                </div>
                {forgotError && (
                  <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-2 rounded-lg text-sm">
                    {forgotError}
                  </div>
                )}
                {forgotSuccess && (
                  <div className="bg-green-500/20 border border-green-500 text-green-300 px-4 py-2 rounded-lg text-sm">
                    {forgotSuccess}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full py-3 px-4 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {forgotLoading ? t('auth.sending') : t('auth.sendResetLink')}
                </button>
              </form>

              {/* No-email path — players only */}
              {!isAdmin && (
                <div className="mt-5 pt-4 border-t border-white/10">
                  <button
                    type="button"
                    className="w-full text-sm text-gray-400 hover:text-blue-300 transition text-left"
                    onClick={() => setShowNoEmail(v => !v)}
                  >
                    {showNoEmail ? '▲ Hide' : "▼ Don't have an email? Request admin to add one"}
                  </button>
                  {showNoEmail && (
                    <form onSubmit={handleNoEmailSubmit} className="mt-4 space-y-3">
                      <p className="text-xs text-gray-400">Enter your name, Player ID, and a new email address. A ticket will be sent to the admin for review.</p>
                      <input
                        type="text"
                        value={noEmailName}
                        onChange={e => setNoEmailName(e.target.value)}
                        required
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Your full name"
                      />
                      <input
                        type="text"
                        value={noEmailId}
                        onChange={e => setNoEmailId(e.target.value)}
                        required
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Your Bowler ID (e.g. 142)"
                      />
                      <input
                        type="email"
                        value={noEmailNewEmail}
                        onChange={e => setNoEmailNewEmail(e.target.value)}
                        required
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="New email address"
                      />
                      {noEmailError && <p className="text-red-400 text-xs">{noEmailError}</p>}
                      {noEmailSuccess && <p className="text-green-400 text-xs">{noEmailSuccess}</p>}
                      <button
                        type="submit"
                        disabled={noEmailLoading}
                        className="w-full py-2 px-4 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 transition disabled:opacity-50 text-sm"
                      >
                        {noEmailLoading ? 'Sending...' : 'Request Admin to Enter Email'}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
